import imageUrlBuilder from '@sanity/image-url'
import {sanityClient} from './client'
import type {SanityImage} from '@/types/sanity'

const builder = imageUrlBuilder(sanityClient)

type ImageUrlBuilder = ReturnType<typeof builder.image>

const emptyImageBuilder = new Proxy(
  {
    url: () => '',
  },
  {
    get(target, property) {
      if (property in target) {
        return target[property as keyof typeof target]
      }

      return () => emptyImageBuilder
    },
  },
) as ImageUrlBuilder

export const hasImageAsset = (source?: SanityImage | null) => Boolean(source?.asset?._ref)

/**
 * `auto('format')` is the default rather than a per-call opt-in because the
 * parameter exists precisely so it does not matter what editors upload. Sanity
 * negotiates on the Accept header, so browsers that support WebP or AVIF get
 * it and everything else still receives the original format.
 *
 * Measured on production 2026-09-14, the NO homepage hero: 3,232,329 bytes as
 * PNG against 56,454 as WebP. Lighthouse scored that page at 56 on mobile with
 * 4,453 KiB of `modern-image-formats` savings across 68 Sanity requests, none
 * of which carried the parameter (JO2-206).
 *
 * Callers can still override with `.auto()` or an explicit `.format()`.
 */
export const urlForImage = (source?: SanityImage | null) =>
  hasImageAsset(source) ? builder.image(source as SanityImage).auto('format') : emptyImageBuilder

export const getImageDimensions = (
  source?: SanityImage,
): {width: number; height: number} | null => {
  const ref = source?.asset?._ref
  if (!ref) return null

  const match = ref.match(/-(\d+)x(\d+)-/)
  if (!match) return null

  const width = Number.parseInt(match[1], 10)
  const height = Number.parseInt(match[2], 10)

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null
  }

  return {width, height}
}

const DEFAULT_OBJECT_POSITION = '50% 50%'

const toPercent = (value: number) => {
  const clamped = Math.max(0, Math.min(1, value))
  return `${Number((clamped * 100).toFixed(2))}%`
}

/**
 * CSS `object-position` derived from the Sanity image's hotspot.
 *
 * The `@sanity/image-url` builder already respects hotspot+crop when
 * `.fit('crop')` is called on it, so the actual served image URL is
 * already focus-aware. This helper handles the OTHER side of the
 * equation: when an `<Image>` element renders with `object-cover`, the
 * browser-side crop falls back to centre unless we tell it where the
 * editor's focus point is.
 *
 * Returns the default centre position (`50% 50%`) when:
 *  - no source is provided
 *  - hotspot isn't set on the asset (current state — hotspot is enabled
 *    via the imageWithAlt schema flip that Tom owns)
 *  - hotspot.x / hotspot.y are missing or non-numeric
 *
 * Once hotspot is enabled in the schema and editors start placing focus
 * points, every consumer that wires this helper into its `<Image>`
 * style respects the choice automatically — no per-component code
 * change required at flip-time.
 *
 * Use as inline style on the `<Image>` (since Tailwind can't synthesise
 * arbitrary percentages safely at build time):
 *
 *     <Image
 *       src={urlForImage(asset).fit('crop').url()}
 *       className="object-cover"
 *       style={{objectPosition: objectPositionFromHotspot(asset)}}
 *       ...
 *     />
 */
export const objectPositionFromHotspot = (source?: SanityImage | null): string => {
  if (!source) return DEFAULT_OBJECT_POSITION

  const hotspot = (source as SanityImage & {hotspot?: {x?: number; y?: number}}).hotspot
  if (!hotspot) return DEFAULT_OBJECT_POSITION

  const x = typeof hotspot.x === 'number' ? hotspot.x : Number(hotspot.x)
  const y = typeof hotspot.y === 'number' ? hotspot.y : Number(hotspot.y)

  if (!Number.isFinite(x) || !Number.isFinite(y)) return DEFAULT_OBJECT_POSITION

  return `${toPercent(x)} ${toPercent(y)}`
}
